<script setup lang="ts">
import { computed, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useField, useIsFormDirty } from 'vee-validate';
import type {
  ProjectModuleDto,
  ProjectConsultationListItemDto,
  ProjectModuleConsultationConfigDto,
  Locales,
  ThemeHost,
} from '@/services/types/api.types';
import { useProjectStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { getTranslation } from '@/composables/data-display-utils';
import { useConfigurationStore } from '@/stores/configurations.store';
import ReorderableTable from '@/components/ReorderableTable.vue';
import StandardToggle from '../StandardToggle.vue';
import ImageField from '@/components/ImageField.vue';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import type { FileInfo } from '@/services/types/file.types';
import { useTheme } from '@/composables/useTheme';
import { ModuleTypeEnum } from '@/types/modules';

// CONST
const { t } = useI18n();
const projectStore = useProjectStore();
const { consultations, isLoading } = storeToRefs(projectStore);
const isProjectTOCEnabled = useConfigurationStore().isFeatureEnabled('projectTOC');

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  editLocale: Locales;
  module: ProjectModuleDto;
  values: ProjectModuleDto;
  themeKey: string;
  themeHost: ThemeHost;
}>();

//
const emit = defineEmits<{
  (e: 'saveImage', options: SavingOptions): void;
  (e: 'deleteImage'): void;
  (e: 'reset-consultation'): void;
  (e: 'set-consultation', consultation: ProjectConsultationListItemDto): void;
}>();

const moduleConfig = computed(
  () => (props.module.config as ProjectModuleConsultationConfigDto) || ({} as ProjectModuleConsultationConfigDto),
);
const file = computed<FileInfo | null>(() => (moduleConfig.value.image as FileInfo) ?? null);

const { value: useConsultationBanner } = useField<boolean>('config.useConsultationBanner');
const { value: image } = useField<FileInfo | null>('config.image');
const isDirty = useIsFormDirty();

const themeConfiguration = useTheme(toRef(props, 'themeHost'), toRef(props, 'themeKey'));
const cropping = themeConfiguration.getCroppingInfo(ModuleTypeEnum.Consultation, 1);

// FUNCTIONS
function resetTitle() {
  emit('reset-consultation');
}

function goToConsultation() {
  window.open(`/consultations/${props.module.config.consultationId}`, '_blank');
}

const handleSaveImage = async (options: SavingOptions) => {
  emit('saveImage', options);
};

const handleClearImage = async () => {
  emit('deleteImage');
};

const handleRowClick = (row: ProjectConsultationListItemDto) => {
  emit('set-consultation', row);
};

const fetchConsultations = async (request: any) => {
  // Only a project hosts a consultation block, so its parent is the project to search.
  await projectStore.searchConsultations(props.module.parent_id, request.page, request.rowsPerPage, request.q);
};

// COMPUTED
const hasConsultationSet = computed(() => {
  if (!props.values || !props.values.title) return false;
  return Object.values(props.values.title).some((value) => value && value !== '');
});

const consultationTitle = computed(() => {
  return getTranslation(props.values, 'title', props.languageEnabled);
});

const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: any) => getTranslation(row, 'title', props.languageEnabled),
    align: 'left',
  },
]);

// WATCH
watch(
  () => hasConsultationSet.value,
  async (newValue) => {
    if (newValue === false) {
      await fetchConsultations({ page: 1, pageSize: 5, q: null });
    }
  },
);

watch(
  () => useConsultationBanner.value,
  (newValue) => {
    if (newValue) image.value = null;
  },
);
</script>
<template>
  <div class="module-consultation">
    <div v-if="hasConsultationSet" class="module-consultation__input">
      <q-input
        :label="t('global.label.title')"
        v-model="consultationTitle"
        :disable="hasConsultationSet"
        outlined
        class="full-width"
      />
      <q-icon name="exit_to_app" @click="goToConsultation" />
      <q-icon name="close" @click="resetTitle" />
    </div>
    <div v-else>
      <reorderable-table
        :rows="consultations.consultationListItems"
        :columns="columns"
        :loading="isLoading"
        itemKey="id"
        @row-click="handleRowClick"
        :disable-add-button="true"
        :disable-delete-button="true"
        :disableOrderable="true"
        :pagination-enabled="true"
        :pagination="consultations.pagination"
        @request="fetchConsultations"
        :search-enabled="true"
      />
    </div>
    <standard-toggle
      v-if="isProjectTOCEnabled"
      name="config.includeInTableOfContents"
      :label="t('project-module.admin.includeInTableOfContents')"
      outlined
    />
    <standard-toggle
      name="config.useConsultationBanner"
      :label="t('project-module.consultation.useConsultationBanner')"
      outlined
    />
    <div v-if="!useConsultationBanner">
      <image-field
        alt-text-name="config.image.metadata.image.altText"
        version="v2"
        :cropping="cropping"
        :file="file!"
        :form-dirty="isDirty"
        :label="t('form.question.image.label')"
        :language-enabled="languageEnabled"
        @clear-image="handleClearImage"
        @save-image="handleSaveImage"
      />
    </div>
  </div>
</template>
<style lang="scss">
.module-consultation {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  &__input {
    display: flex;
    flex-direction: row;
    gap: 10px;
    width: 100%;

    .q-icon {
      font-size: $medium-title;
      padding: 18px 5px;
      cursor: pointer;
    }
  }
}
</style>
